const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/defs/actionTypes.ts';
import Imm from 'immutable';
import { Action } from 'redux';

const emptyMap = Imm.Map<string, any>();

export type ReducerState = Imm.Map<string, any>;

/**
 * Base values of KISAPI meta objects. Do not add anything except base functionality covered by kisapiMiddleware.
 */
export interface IKISAPIMeta {
    etag?: string;
    suppressErrorMsg?: boolean;
    showDlgWhenResultHasMoreRows?: boolean;
    responseStatus?: number;
    id?: string; // Can be used to identify the request so loading can be tracked
}

/**
 * Base values of any KISAPI action
 */
export interface IKISAPIAction extends Action {
    payload?: any;
    meta: IKISAPIMeta;
}

/**
 * Use this generic type to quickly determine the KISAPI action type based on
 * the result of an action function.
 */
export type KisApiAction<TActionFn extends (...args: any) => any = (...args: any) => any> = IKISAPIAction & {
    meta: ReturnType<TActionFn>['meta'];
};

/**
 * An action handler is a reducer or subreducer
 */
export type ActionHandler<TAction extends Action<string> = Action<string>> = (
    state: ReducerState,
    action: TAction
) => ReducerState;

/**
 * Maps action types to their corresponding handlers
 */
export interface IActionHandlersMap {
    [actionType: string]: ActionHandler;
}

/**
 * The three action handlers of a particular KISAPI action wrapped in an object
 */
export interface IKisApiHandlersMap<TAction extends IKISAPIAction = KisApiAction> {
    pending: ActionHandler<TAction>;
    success: ActionHandler<TAction>;
    failure: ActionHandler<TAction>;
}

export interface IKisApiActionsMap {
    pending: string;
    success: string;
    failure: string;
    combined: string;
}

/**
 * @return An object containing the three action types used by KISAPI actions
 */
export function kisapiActionTypes(prefix: string): IKisApiActionsMap {
    const pending = `${prefix}_PENDING`;
    const success = `${prefix}_SUCCESS`;
    const failure = `${prefix}_FAILURE`;
    return {
        pending,
        success,
        failure,
        combined: [pending, success, failure].join(','),
    };
}

/**
 * Helper function to create a reducer from an action handlers map.
 * This may be used for a top-level reducer as well as for a nested subreducer.
 * @param handlers A map of handler functions mapped to their action types
 * @param initialState What to return when state is undefined.
 * @param part If this is undefined, the reducer will act on the whole state;
 *    if this is set to some string, the reducer will act on the subtree of that name only
 * @param combine To merge other reducers into the new reducer
 * @param fallback Fallback handler to catch actions that do not have any handler
 */
export function makeReducer(
    handlers: IActionHandlersMap,
    initialState: ReducerState = emptyMap,
    part?: string,
    combine?: ActionHandler[],
    fallback?: ActionHandler
) {
    return (state: ReducerState | undefined, action: Action) => {
        if (!state) {
            if (part) {
                state = emptyMap.set(part, initialState);
            } else {
                state = initialState;
            }

            if (combine) {
                combine.forEach(reducer => {
                    state = (state as ReducerState).merge(reducer(emptyMap, action));
                });
            }

            return state;
        }

        if (combine) {
            combine.forEach(reducer => {
                state = reducer(state as ReducerState, action);
            });
        }

        let hnd: ActionHandler | undefined = handlers[action?.type];

        if (!hnd) {
            hnd = fallback;
        }

        if (!hnd) {
            return state;
        } else if (!part) {
            return hnd(state, action);
        } else {
            return state.set(part, hnd(state.get(part) || emptyMap, action));
        }
    };
}
